import { useCallback, useEffect, useState } from 'react'
import {
  claimDomain,
  completeSSOConnection,
  createSSOConnection,
  orgDomains,
  removeDomain,
  removeSSOConnection,
  disableSSO,
  enableSSO,
  ssoSettings,
  verifyDomain,
  type AuthenticatedSession,
  type OrgDomain,
  type SSOConnection,
  type SSOSettings,
} from '../../api'
import { AdminPage } from './AdminPage'
import { ConfirmSheet } from '../../components/ConfirmSheet'
import { CopyField } from '../../components/CopyField'

/**
 * Letting a company's staff sign in with their own identity provider.
 *
 * The whole of the setup is here because the whole of it is one conversation
 * between two people: an administrator, and whoever runs their identity
 * provider. It reads in that order. Prove the domains your staff use. Take
 * these two values to your IT team. Paste back the three they give you. Done.
 *
 * Proving comes first and cannot be skipped. Claiming a domain says "everybody
 * with an address here signs in through my identity provider", and unproved
 * that is a claim over a namespace somebody else owns: it would send real
 * people at that domain to a provider that is not theirs. The one exception is
 * the domain the administrator's own address is at, which docsdone verified by
 * emailing a code there, and asking for a DNS record as well would be theatre.
 *
 * The page never names the provider docsdone uses to reach their identity
 * provider, because it is our supplier and not theirs. It is not pretending it
 * does not exist: the two values they take to their IT team are URLs at that
 * supplier's host, because that is where the assertion is posted and there is
 * nowhere else it could go. What the page avoids is making a customer learn a
 * third company's product vocabulary to configure their own Okta.
 */
export function OrgSignIn({ session }: { session: AuthenticatedSession }) {
  return (
    <AdminPage
      session={session}
      title="Sign-in"
      intro="Staff sign in with your company's identity provider, or with a code sent to their email."
    >
      {(orgId) => <SignInBody orgId={orgId} />}
    </AdminPage>
  )
}

function SignInBody({ orgId }: { orgId: string }) {
  // null means "still asking", which is not the same as "off": a switch that
  // renders off and then flips on is a switch somebody will click twice.
  const [state, setState] = useState<SSOSettings | null>(null)
  const [domains, setDomains] = useState<OrgDomain[] | null>(null)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    ssoSettings(orgId)
      .then(setState)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not read the sign-in settings.'))
    orgDomains(orgId)
      .then(setDomains)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not read the domains.'))
  }, [orgId])
  useEffect(load, [load])

  if (!state || !domains) {
    return <div className="card"><p className="muted">Loading…</p></div>
  }
  if (!state.configured) {
    return (
      <div className="card">
        <h2>Not available on this deployment</h2>
        <p className="small muted">
          Company sign-in is not configured.
        </p>
      </div>
    )
  }
  const proved = domains.filter((d) => d.verified)
  return (
    <>
      {error && <div className="notice notice-bad signin-notice">{error}</div>}
      <Domains orgId={orgId} domains={domains} onChange={setDomains} onError={setError} />
      {state.enabled ? (
        <Connected orgId={orgId} state={state} onChange={setState} onError={setError} />
      ) : proved.length ? (
        <TurnOn orgId={orgId} proved={proved} onChange={setState} onError={setError} />
      ) : null}
    </>
  )
}

/**
 * The domains a company has proved, and the one it is proving.
 *
 * The record goes on a subdomain rather than the apex, because an apex TXT
 * record shares space with SPF and with every other service that wants one,
 * and asking somebody to edit their apex is asking them to risk their mail.
 */
function Domains({
  orgId,
  domains,
  onChange,
  onError,
}: {
  orgId: string
  domains: OrgDomain[]
  onChange: (d: OrgDomain[]) => void
  onError: Fail
}) {
  const [adding, setAdding] = useState('')
  const [busy, setBusy] = useState('')

  async function add(e: React.FormEvent) {
    e.preventDefault()
    setBusy('add')
    onError('')
    try {
      const d = await claimDomain(orgId, adding)
      onChange([...domains.filter((x) => x.id !== d.id), d])
      setAdding('')
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not add that domain.')
    } finally {
      setBusy('')
    }
  }

  async function check(id: string) {
    setBusy(id)
    onError('')
    try {
      const d = await verifyDomain(orgId, id)
      onChange(domains.map((x) => (x.id === d.id ? d : x)))
    } catch (err) {
      onError(err instanceof Error ? err.message : 'That record is not visible yet.')
    } finally {
      setBusy('')
    }
  }

  async function drop(id: string) {
    setBusy(id)
    onError('')
    try {
      onChange(await removeDomain(orgId, id))
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not remove that domain.')
    } finally {
      setBusy('')
    }
  }

  return (
    <div className="card">
      <h2>Your domains</h2>
      <p className="small muted">
        Staff at a verified domain sign in with your identity provider.
      </p>

      {domains.map((d) => (
        <div key={d.id} className="stack-tight setup-step">
          <div className="row-between">
            <strong>{d.domain}</strong>
            <span className={`badge badge-${d.verified ? 'completed' : 'sent'}`}>
              {d.verified ? 'Verified' : 'Not verified'}
            </span>
          </div>
          {!d.verified && (
            <>
              <p className="small muted">Add this TXT record to your DNS, then check it.</p>
              <CopyField label="Record name" value={d.record} />
              <CopyField label="Value" value={d.value} />
            </>
          )}
          <div className="row-tight">
            {!d.verified && (
              <button
                className="btn btn-secondary btn-small"
                disabled={busy === d.id}
                onClick={() => void check(d.id)}
              >
                {busy === d.id ? 'Checking…' : 'Check now'}
              </button>
            )}
            <button
              className="btn btn-ghost btn-small"
              disabled={busy === d.id}
              onClick={() => void drop(d.id)}
            >
              Remove
            </button>
          </div>
        </div>
      ))}

      <form onSubmit={add} className="setup-step">
        <label htmlFor="add-domain">Add a domain</label>
        <div className="field-with-action">
          <input
            id="add-domain"
            type="text"
            placeholder="acme.com"
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
          />
          <button id="add-domain-submit" className="btn btn-secondary" type="submit" disabled={busy === 'add' || !adding.trim()}>
            {busy === 'add' ? 'Adding…' : 'Add'}
          </button>
        </div>
      </form>
    </div>
  )
}

type Setter = (s: SSOSettings) => void
type Fail = (s: string) => void

/**
 * Turning it on, once at least one domain is proved. It says the domains'
 * staff become members: a login through the identity provider makes the
 * person a member with no invitation (review 1, web-5).
 */
export function TurnOn({
  orgId,
  proved,
  onChange,
  onError,
}: {
  orgId: string
  proved: OrgDomain[]
  onChange: Setter
  onError: Fail
}) {
  const [chosen, setChosen] = useState<string[]>(proved.map((d) => d.domain))
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    onError('')
    try {
      onChange(await enableSSO(orgId, chosen))
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not turn company sign-in on.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <h2>Turn on company sign-in</h2>
      <p className="small muted">
        People at the selected domains sign in with your identity provider, and become members of this organization when they do. Everyone else receives a code by email.
      </p>
      <form onSubmit={submit}>
        {proved.map((d) => (
          <label key={d.id} className="row-tight">
            <input
              type="checkbox"
              checked={chosen.includes(d.domain)}
              onChange={(e) =>
                setChosen(
                  e.target.checked
                    ? [...chosen, d.domain]
                    : chosen.filter((x) => x !== d.domain),
                )
              }
            />
            <span>{d.domain}</span>
          </label>
        ))}
        <button id="sso-turn-on" className="btn signin-submit" type="submit" disabled={busy || !chosen.length}>
          {busy ? <><span className="spinner" /> Turning on…</> : 'Turn on'}
        </button>
      </form>
    </div>
  )
}

/** Steps two and three, plus the way back out. */
function Connected({
  orgId,
  state,
  onChange,
  onError,
}: {
  orgId: string
  state: SSOSettings
  onChange: Setter
  onError: Fail
}) {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)

  async function turnOff() {
    setBusy(true)
    onError('')
    try {
      onChange(await disableSSO(orgId))
      setConfirming(false)
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not turn company sign-in off.')
    } finally {
      setBusy(false)
    }
  }

  // The provider could not be reached. Company sign-in is still on and still
  // works for anybody signing in, because that path does not go through this
  // page; what cannot be shown is the detail that lives at the provider. The
  // one action that still works without it is turning it off, and it is the
  // one somebody in this situation may want.
  if (!state.reachable) {
    return (
      <>
        <div className="notice notice-warn signin-notice">
          Company sign-in is on for this organization, but the sign-in provider could not be
          reached just now, so its details cannot be shown. Signing in is unaffected. Try again in
          a moment.
        </div>
        <div className="card danger-zone">
          <h2>Turn off company sign-in</h2>
          <p className="small muted">
            Staff sign in with a code sent to their email. Accounts are kept.
          </p>
          <button id="sso-turn-off" className="btn btn-danger" onClick={() => setConfirming(true)}>
            Turn off
          </button>
        </div>
        {confirming && (
          <ConfirmSheet
            title="Turn off company sign-in?"
            confirmLabel="Turn it off"
            busy={busy}
            onConfirm={() => void turnOff()}
            onClose={() => setConfirming(false)}
          >
            <p className="small muted">
              Everyone keeps their account and signs in with a code.
            </p>
          </ConfirmSheet>
        )}
      </>
    )
  }

  return (
    <>
      <div className={`notice ${state.ready ? 'notice-good' : 'notice-warn'} signin-notice`}>
        {state.ready
          ? `Staff at ${state.domains.join(', ')} sign in with your identity provider.`
          : 'Complete the connection. Staff sign in with a code until it is complete.'}
      </div>

      {state.connections.map((c: SSOConnection) => (
        <ConnectionCard key={c.id} orgId={orgId} connection={c} onChange={onChange} onError={onError} />
      ))}

      {state.connections.length === 0 ? (
        <AddConnection orgId={orgId} onChange={onChange} onError={onError} />
      ) : (
        // Silence here would read as a missing feature. One identity provider
        // at a time is a rule, and a rule stated is a rule somebody can work
        // with.
        <p className="small faint">
          One identity provider supported at a time. Remove to reconfigure.
        </p>
      )}

      <div className="card danger-zone">
        <h2>Turn off company sign-in</h2>
        <p className="small muted">
          Staff sign in with a code sent to their email. Accounts, documents, and memberships stay as they are.
        </p>
        <button id="sso-turn-off" className="btn btn-danger" onClick={() => setConfirming(true)}>Turn off</button>
      </div>

      {confirming && (
        <ConfirmSheet
          title="Turn off company sign-in?"
          confirmLabel="Turn it off"
          busy={busy}
          onConfirm={() => void turnOff()}
          onClose={() => setConfirming(false)}
        >
          <p className="small muted">
            The connection is removed. Everyone keeps their account and signs in with a code.
          </p>
        </ConfirmSheet>
      )}
    </>
  )
}

/** The products docsdone can name on a certificate. Anything else still works
 * and is described honestly as "an identity provider" instead. */
const PROVIDERS = [
  ['okta', 'Okta'],
  ['microsoft-entra', 'Microsoft Entra ID'],
  ['google-workspace', 'Google Workspace'],
  ['onelogin', 'OneLogin'],
  ['pingfederate', 'PingFederate'],
  ['jumpcloud', 'JumpCloud'],
  ['duo', 'Duo'],
  ['keycloak', 'Keycloak'],
  ['cyberark', 'CyberArk'],
  ['classlink', 'ClassLink'],
  ['miniorange', 'miniOrange'],
  ['rippling', 'Rippling'],
  ['salesforce', 'Salesforce'],
  ['shibboleth', 'Shibboleth'],
  ['', 'Something else'],
] as const

function AddConnection({ orgId, onChange, onError }: { orgId: string; onChange: Setter; onError: Fail }) {
  const [provider, setProvider] = useState('okta')
  const [protocol, setProtocol] = useState<'saml' | 'oidc'>('saml')
  const [busy, setBusy] = useState(false)

  async function add() {
    setBusy(true)
    onError('')
    try {
      const label = PROVIDERS.find(([v]) => v === provider)?.[1] ?? 'Company sign-in'
      onChange(await createSSOConnection(orgId, { display_name: label, provider, protocol }))
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not start that connection.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <h2>Connect your identity provider</h2>
      <p className="small muted">
        The name is printed on the certificate for documents your staff send.
      </p>
      <div className="grid-2">
        <div>
          <label htmlFor="sso-provider">Identity provider</label>
          <select id="sso-provider" value={provider} onChange={(e) => setProvider(e.target.value)}>
            {PROVIDERS.map(([value, label]) => (
              <option key={value || 'other'} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="sso-protocol">Connection type</label>
          <select
            id="sso-protocol"
            value={protocol}
            onChange={(e) => setProtocol(e.target.value as 'saml' | 'oidc')}
          >
            <option value="saml">SAML</option>
            <option value="oidc">OpenID Connect</option>
          </select>
          <p className="small faint signin-hint">SAML is the standard connection type.</p>
        </div>
      </div>
      <button id="sso-add-connection" className="btn signin-submit" disabled={busy} onClick={() => void add()}>
        {busy ? <><span className="spinner" /> Starting…</> : 'Continue'}
      </button>
    </div>
  )
}

/** One connection: what to give them, and what to paste back. */
function ConnectionCard({
  orgId,
  connection,
  onChange,
  onError,
}: {
  orgId: string
  connection: SSOConnection
  onChange: Setter
  onError: Fail
}) {
  const saml = connection.protocol === 'saml'
  const [issuer, setIssuer] = useState(connection.issuer)
  const [ssoUrl, setSsoUrl] = useState(connection.sso_url)
  const [certificate, setCertificate] = useState('')
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [busy, setBusy] = useState(false)
  const [removing, setRemoving] = useState(false)

  async function remove() {
    setBusy(true)
    onError('')
    try {
      onChange(await removeSSOConnection(orgId, connection.id))
      setRemoving(false)
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not remove that connection.')
    } finally {
      setBusy(false)
    }
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    onError('')
    try {
      onChange(
        await completeSSOConnection(orgId, connection.id, {
          issuer,
          ...(saml ? { sso_url: ssoUrl, certificate } : { client_id: clientId, client_secret: clientSecret }),
        }),
      )
      setCertificate('')
      setClientSecret('')
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Those details were not accepted.')
    } finally {
      setBusy(false)
    }
  }

  const ready = saml
    ? issuer.trim() && ssoUrl.trim() && certificate.trim()
    : issuer.trim() && clientId.trim() && clientSecret.trim()

  return (
    <div className="card">
      <div className="row-between">
        <h2>{connection.provider}</h2>
        <span className={`badge badge-${connection.status === 'active' ? 'completed' : 'sent'}`}>
          {connection.status === 'active' ? 'Connected' : 'Not finished'}
        </span>
      </div>

      <h3 className="small setup-step">Give these to your IT team</h3>
      <p className="small muted">
        Create an application in {connection.provider} and enter these two values.
      </p>
      <CopyField label={saml ? 'Sign-on URL (ACS)' : 'Redirect URL'} value={connection.sign_in_url} />
      <CopyField label={saml ? 'Audience URI (Entity ID)' : 'Client ID'} value={connection.identifier} />

      <h3 className="small setup-step">Paste back what they give you</h3>
      <form onSubmit={save}>
        <label htmlFor={`sso-issuer-${connection.id}`}>
          {saml ? 'Identity provider issuer (Entity ID)' : 'Issuer URL'}
        </label>
        <input
          id={`sso-issuer-${connection.id}`}
          type="text"
          value={issuer}
          onChange={(e) => setIssuer(e.target.value)}
        />
        {saml ? (
          <>
            <label htmlFor={`sso-url-${connection.id}`}>Sign-on URL</label>
            <input
              id={`sso-url-${connection.id}`}
              type="text"
              placeholder="https://…"
              value={ssoUrl}
              onChange={(e) => setSsoUrl(e.target.value)}
            />
            <label htmlFor={`sso-cert-${connection.id}`}>Signing certificate</label>
            <textarea
              id={`sso-cert-${connection.id}`}
              rows={5}
              placeholder="-----BEGIN CERTIFICATE-----"
              value={certificate}
              onChange={(e) => setCertificate(e.target.value)}
            />
          </>
        ) : (
          <>
            <label htmlFor={`sso-client-${connection.id}`}>Client ID</label>
            <input
              id={`sso-client-${connection.id}`}
              type="text"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            />
            <label htmlFor={`sso-secret-${connection.id}`}>Client secret</label>
            <input
              id={`sso-secret-${connection.id}`}
              type="password"
              autoComplete="off"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
            />
          </>
        )}
        <p className="small faint signin-hint">
          {connection.status === 'active'
            ? 'Connected. Replace the certificate before it expires.'
            : 'Staff are not redirected until these details are saved.'}
        </p>
        <div className="row-between signin-submit">
          <button id="sso-connect" className="btn" type="submit" disabled={busy || !ready}>
            {busy ? (
              <><span className="spinner" /> Connecting…</>
            ) : connection.status === 'active' ? (
              'Update'
            ) : (
              'Connect'
            )}
          </button>
          {/* The way out of the wrong choice. It is a link rather than a
              button because it is the quiet option on a card whose job is the
              one beside it. */}
          <button id="sso-remove" className="btn btn-link" type="button" onClick={() => setRemoving(true)}>
            Remove this connection
          </button>
        </div>
      </form>

      {removing && (
        <ConfirmSheet
          title={`Remove the ${connection.provider} connection?`}
          confirmLabel="Remove it"
          busy={busy}
          onConfirm={() => void remove()}
          onClose={() => setRemoving(false)}
        >
          <p className="small muted">
            Company sign-in stays on. Until another provider is connected, staff sign in with a code sent to their email.
          </p>
          <p className="small muted">
            Accounts are kept. Documents already signed still name this connection.
          </p>
        </ConfirmSheet>
      )}
    </div>
  )
}
